import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import Api from "./api";
import Service from "./base";
import DutyStatusLog, { IDutyStatusLog, STANDARD_STATUSES } from "../model/DutyStatusLog";
import { DutyStatusLogService, IDailyOdometer } from "../model/DutyStatusLogs";
import { OdometerData } from "../sync/hos.OdometerData";
import { handleApi } from "../utils/error";
import { IApiError } from "../utils/api.interfaces";
import { extend } from "../utils/vanilla";
import { type IValidation, type IViolation, type THosStatus, compareDatesWithSequenceIds } from "hos-tools";
import { DriveStore } from "../store/reducers";
import { HistoricalDayActions } from "../store/hosLogs/actions";
import { NetworkStates } from "../store/mobile/types";
import * as logger from "../utils/logger";
import { getCurrentUser } from "../utils/storage/storage";
import { getServerDate } from "../utils/storage/date";
import PerformanceProfilerFactory from "../utils/performanceProfiler/performanceProfilerFactory";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import { isPlaceholderCertified } from "../utils/placeholderVerification";

export const MAX_HISTORICAL_DAYS = 15;

export interface IHistoricalDayResult {
    userHosLogs: DutyStatusLog[];
    systemHosLogs: DutyStatusLog[];
    /**
     * True when the Certify lookup failed, so a continuation entry may be shown uncertified for a
     * certified day. Such a day is returned but not cached, so the next visit retries.
     */
    certifyLookupFailed?: boolean;
}

/** The per-call half of a DutyStatusLog search; {@link HistoricalDayLoader.fetchLogs} adds the rest. */
interface IDutyStatusLogDaySearch {
    fromDate: Date;
    toDate: Date;
    statuses?: THosStatus[];
    includeBoundaryLogs: boolean;
    /** Override the shared defaults (all true) when a query doesn't need the extra server work. */
    includeCompliance?: boolean;
    includeLocations?: boolean;
    includeModifications?: boolean;
}

export interface IHistoricalDayDataResult extends IHistoricalDayResult {
    violations: IViolation[];
    odometer?: IDailyOdometer;
}

/**
 * Service for loading HOS logs for historical days (outside the current sync window).
 */
export class HistoricalDayLoader extends Service {
    // Own a dedicated OdometerData instance rather than sharing the sync's: OdometerData keeps
    // mutable per-run fields (multiCall etc.), so a concurrent historical fetch on the sync's
    // instance would corrupt the sync's in-flight multicall.
    private readonly odometerData: OdometerData;

    constructor(
        private readonly api: Api,
        private readonly dutyStatusLogService: DutyStatusLogService,
        private readonly store: DriveStore
    ) {
        super();
        this.odometerData = new OdometerData(this.api, this.store);
    }

    /**
     * Loads DutyStatusLogs and DriverRegulation for a historical day in parallel,
     * then caches the combined result in Redux.
     *
     * Date boundary computation is the caller's responsibility:
     * @param dayFromDate - Start of the selected day in the user's timezone.
     * @param dayToDate - End of the selected day in the user's timezone.
     * @param regulationsFromDate - Start of the lookback window (7 days USA / 14 days Canada).
     * @param userId - The driver's user ID.
     * @param dateKey - YYYYMMDD string for the day (e.g. "20260520"), caller-provided.
     */
    loadHistoricalDayData(
        dayFromDate: Date,
        dayToDate: Date,
        regulationsFromDate: Date,
        userId: string,
        dateKey: string
    ): Promise<IHistoricalDayDataResult> {
        const currentUser = getCurrentUser();
        const rawEntry = this.store.getState().historicalDayTracker[userId]?.[dateKey];
        if (rawEntry) {
            logger.event(logger.Event.HISTORICALDAYCACHE, JSON.stringify({ result: "hit", dateKey }), currentUser);
            return Promise.resolve({
                userHosLogs: rawEntry.hosLogs.map(log => new DutyStatusLog(log)),
                systemHosLogs: rawEntry.systemLogs.map(log => new DutyStatusLog(log)),
                violations: extend(true, [], rawEntry.violations),
                odometer: extend(true, {}, rawEntry.odometer ?? {})
            });
        }

        logger.event(logger.Event.HISTORICALDAYCACHE, JSON.stringify({ result: "miss", dateKey }), currentUser);

        if (this.store.getState().mobile.online !== NetworkStates.ONLINE) {
            return Promise.reject(new Error(NetworkStates.OFFLINE));
        }

        return Promise.all([
            this.loadHistoricalDay(dayFromDate, dayToDate, userId, dateKey),
            this.loadRegulations(regulationsFromDate, dayToDate, userId)
        ]).then(([dayResult, regulation]) => {
            const violations = regulation?.violations ?? [];
            // Odometer needs the day's logs, so it runs here (after loadHistoricalDay resolves) rather
            // than inside it. Distances are written in place on dayResult's log objects.
            return this.fetchDayOdometer(dayResult.userHosLogs, dayResult.systemHosLogs).then(odometer => {
                const combined: IHistoricalDayDataResult = { ...dayResult, violations, odometer };
                logger.event(logger.Event.HISTORICALDAYAPI, JSON.stringify({ result: "success", dateKey }), currentUser);
                // A day whose Certify lookup failed may show a certified day as Uncertified; don't
                // persist that, so the next visit fetches certification again.
                if (!dayResult.certifyLookupFailed) {
                    this.cacheDay(userId, dateKey, combined);
                }
                return combined;
            });
        }).catch((err: IApiError) => {
            logger.event(
                logger.Event.HISTORICALDAYAPI,
                JSON.stringify({ result: "failure", dateKey, error: err?.message ?? "" }),
                currentUser
            );
            return Promise.reject(err);
        });
    }

    /**
     * Loads DutyStatusLogs for a historical day, plus the boundary log needed to render a
     * "continued from previous day" placeholder, in two parallel calls:
     *
     * - the day query itself, scoped strictly to [fromDate, toDate);
     * - a dedicated boundary query (`includeBoundaryLogs`, collapsed to the single instant
     *   `fromDate`) filtered server-side to {@link STANDARD_STATUSES}.
     *
     * The boundary query needs its own status filter because `includeBoundaryLogs` returns only the
     * one log immediately preceding the range. Folding it into the day query returned whichever log
     * happened to be nearest in time — often a control record (Certify/Login/Logoff) that cannot
     * carry a status forward — leaving no candidate and rendering the day as "no logs" even though a
     * real standard-status log existed further back.
     *
     * The continuation placeholder is built and inserted here, so callers get a ready-to-render
     * {@link IHistoricalDayResult} without needing to know about the boundary log.
     *
     * @param fromDate - Start of the selected day in the user's timezone.
     * @param toDate - End of the selected day in the user's timezone.
     * @param userId - The driver's user ID.
     * @param dateKey - YYYYMMDD string for the day; used to make the placeholder id unique.
     */
    loadHistoricalDay(fromDate: Date, toDate: Date, userId: string, dateKey: string): Promise<IHistoricalDayResult> {
        // measureCancelable (not measureAsync) preserves the CancelablePromise return type that
        // callers cancel on navigation; it likewise ends the profiler on resolve/reject/cancel.
        return PerformanceProfilerFactory.getInstance(MeasureName.LoadHistoricalDay).measureCancelable(() => {
            let boundaryLogError: IApiError | null = null;
            return Promise.all([
                this.fetchDayLogs(fromDate, toDate, userId),
                // The boundary log only enables the "continued from previous day" placeholder, so a
                // failure here shouldn't discard the day logs the other call already fetched.
                // The error is captured (not swallowed outright) because a day with no logs of its
                // own has no way to tell "no boundary log" and "boundary fetch failed" apart, and
                // treating a failure as "no boundary log" there would silently reproduce the
                // no-status-filter bug this fix corrects, just via a different path.
                this.fetchBoundaryLog(fromDate, userId).catch((err: IApiError) => {
                    boundaryLogError = err;
                    return null;
                }),
                // Certify records are made after the day they certify, so they sit outside the day
                // query. Like the boundary log, they only decorate the placeholder, so a failure
                // keeps the day logs, but it is flagged so the day isn't cached with a falsely
                // uncertified continuation entry (see loadHistoricalDayData).
                this.fetchCertifyLogs(fromDate, userId).catch(() => null)
            ]).then(([logs, boundaryLog, fetchedCertifyLogs]) => {
                const certifyLookupFailed = fetchedCertifyLogs === null;
                const certifyLogs = fetchedCertifyLogs ?? [];
                // Match getLogsByDate (the in-window logs page): [fromDate, toDate) — toDate is
                // the start of the next day, so the end bound is exclusive to avoid claiming a
                // log that belongs to the next day.
                const fromMs = fromDate.getTime();
                const toMs = toDate.getTime();
                const dayLogs = logs.filter(log => {
                    const t = log.getDate().getTime();
                    return t >= fromMs && t < toMs;
                });
                const separated = this.separateLogs(dayLogs);
                if (boundaryLogError && separated.userHosLogs.length === 0) {
                    // The day has no logs of its own, so the boundary log is the only thing that
                    // could make it non-empty — surface the failure instead of rendering a false
                    // "no logs" day.
                    return Promise.reject(boundaryLogError);
                }
                const placeholder = this.buildContinuationPlaceholder(boundaryLog, fromDate, separated.userHosLogs, dateKey, certifyLogs);
                // Sort chronologically so day-grouping and the graph receive logs in order, with
                // the continuation placeholder (start of day) positioned first.
                const userHosLogs = this.dutyStatusLogService.getSortedHosLogs(
                    placeholder ? [placeholder, ...separated.userHosLogs] : separated.userHosLogs
                );
                return { userHosLogs, systemHosLogs: separated.systemHosLogs, certifyLookupFailed };
            });
        });
    }

    /**
     * Fetches the DriverRegulation record for a date range, which includes violations,
     * availability, and cycle summaries computed server-side.
     * The caller is responsible for providing the correct fromDate (lookback start)
     * and toDate (end of selected day).
     *
     * @param fromDate - Start of the lookback window (7 days for USA, 14 for Canada).
     * @param toDate - End of the selected day.
     * @param userId - The driver's user ID.
     */
    loadRegulations(fromDate: Date, toDate: Date, userId: string): Promise<Partial<IValidation> | null> {
        // measureCancelable (not measureAsync) preserves the CancelablePromise return type; the
        // profiler ends on resolve/reject/cancel.
        return PerformanceProfilerFactory.getInstance(MeasureName.LoadRegulations).measureCancelable(() =>
            this.api.callPromise("Get", {
                typeName: "DriverRegulation",
                search: {
                    userSearch: { id: userId },
                    fromDate,
                    toDate
                }
            }).then((regulations: Partial<IValidation>[]) => {
                if (!regulations?.length) {
                    return null;
                }
                return regulations[0];
            }).catch((err: IApiError) => {
                handleApi(err);
                return Promise.reject(err);
            }));
    }

    /**
     * Removes the given day and every cached day after it for the user, so they re-fetch from the
     * server on next view. Adding or editing a log on a historical day changes the status carried into
     * later days (the "continued from previous day" placeholder built from the boundary log), so those
     * cached entries are stale too. dateKeys are YYYYMMDD, so a lexicographic compare matches
     * chronological order.
     *
     * @param userId - The driver's user ID.
     * @param fromDateKey - YYYYMMDD of the earliest day to invalidate (inclusive).
     */
    invalidateDayAndFollowing(userId: string, fromDateKey: string): void {
        const trackedDays = this.store.getState().historicalDayTracker[userId];
        if (!trackedDays) {
            return;
        }
        Object.keys(trackedDays)
            .filter(dateKey => dateKey >= fromDateKey)
            .forEach(dateKey => this.store.dispatch(HistoricalDayActions.untrackHistoricalDay({ userId, dateKey })));
    }

    // No subscriptions or timers to clean up; required by Service base class.
    destroy() {}

    /**
     * Fetches/computes the per-device odometer start/end bounds for a historical day the same way the
     * in-window sync does, and writes each log's distance in place (consumed by the daily mileage
     * total). Runs after the day's logs are loaded because it needs them: the combined array (user +
     * system logs, sorted ascending) is what the odometer math iterates over.
     *
     * Odometer failures must not block rendering the day's logs, so a failed lookup resolves to {}.
     */
    fetchDayOdometer(userHosLogs: DutyStatusLog[], systemHosLogs: DutyStatusLog[]): Promise<IDailyOdometer> {
        const combined = [...userHosLogs, ...systemHosLogs].sort((a, b) => compareDatesWithSequenceIds(a, b));
        // measureCancelable logs the odometer API call's duration (PERFORMANCE event), mirroring the
        // LoadHistoricalDay/LoadRegulations measures; it ends the profiler on resolve/reject/cancel.
        return PerformanceProfilerFactory.getInstance(MeasureName.LoadHistoricalOdometer).measureCancelable(() =>
            this.odometerData.fetchOdometerForHistoricalLogs(combined));
    }

    /**
     * Runs one DutyStatusLog query for the driver and converts the result. `search` supplies only the
     * parts that differ between callers; the fields every query here shares are filled in below.
     */
    private fetchLogs(userId: string, search: IDutyStatusLogDaySearch): Promise<DutyStatusLog[]> {
        return new Promise<DutyStatusLog[]>((resolve, reject) => {
            this.api.call(
                "Get",
                {
                    typeName: "DutyStatusLog",
                    search: {
                        userSearch: { id: userId },
                        includeCompliance: true,
                        includeModifications: true,
                        includeLocations: true,
                        ...search
                    }
                },
                // null currentUser — logs are already scoped to userId via userSearch
                (result: IDutyStatusLog[]) => resolve(this.dutyStatusLogService.convertDutyStatusLogsFromServer(result, null)),
                (err: IApiError) => {
                    handleApi(err);
                    reject(err);
                }
            );
        });
    }

    /**
     * Fetches the selected day's own logs. `includeBoundaryLogs` is deliberately off: the boundary
     * log is fetched separately by {@link fetchBoundaryLog} so it can be status-filtered.
     */
    private fetchDayLogs(fromDate: Date, toDate: Date, userId: string): Promise<DutyStatusLog[]> {
        return this.fetchLogs(userId, { fromDate, toDate, includeBoundaryLogs: false });
    }

    /**
     * Fetches the log whose status carries into the start of the selected day, or null when there is
     * none. The range is collapsed to the single instant `fromDate` so `includeBoundaryLogs` returns
     * the log active at that moment, and `statuses` restricts the server-side search to statuses that
     * can actually carry forward — without it the nearest log is often a control record that
     * {@link findBoundaryLog} then rejects, leaving no candidate at all.
     */
    private fetchBoundaryLog(fromDate: Date, userId: string): Promise<DutyStatusLog | null> {
        return this.fetchLogs(userId, {
            fromDate,
            toDate: fromDate,
            statuses: STANDARD_STATUSES,
            includeBoundaryLogs: true
        }).then(logs => this.findBoundaryLog(logs, fromDate));
    }

    /**
     * Fetches the driver's Certify records made from the start of the selected day onward. A day is
     * certified after it ends, so the record that certifies it is dated on or after `fromDate`. Only
     * `dateTime` and `verifyDateTime` are used, so the server-side compliance, location and
     * modification work is switched off: the range grows with how far back the day is.
     */
    private fetchCertifyLogs(fromDate: Date, userId: string): Promise<DutyStatusLog[]> {
        return this.fetchLogs(userId, {
            fromDate,
            toDate: getServerDate(),
            statuses: ["Certify"],
            includeBoundaryLogs: false,
            includeCompliance: false,
            includeLocations: false,
            includeModifications: false
        });
    }

    private cacheDay(userId: string, dateKey: string, writeLog: IHistoricalDayDataResult): void {
        const trackedDays = this.store.getState().historicalDayTracker[userId] || {};
        const trackedKeys = Object.keys(trackedDays);

        let totalHosLogs = 0;
        let totalSystemLogs = 0;
        let totalViolations = 0;
        trackedKeys.forEach(key => {
            totalHosLogs += trackedDays[key].hosLogs.length;
            totalSystemLogs += trackedDays[key].systemLogs.length;
            totalViolations += trackedDays[key].violations.length;
        });

        if (trackedKeys.length >= MAX_HISTORICAL_DAYS && !trackedDays[dateKey]) {
            // FIFO eviction: remove the entry with the smallest loadedAt timestamp.
            const oldestKey = trackedKeys.reduce((oldest, key) =>
                trackedDays[key].loadedAt < trackedDays[oldest].loadedAt ? key : oldest);
            const evictedSystemLogsSize = trackedDays[oldestKey].systemLogs?.length ?? 0;
            const evictedHosLogsSize = trackedDays[oldestKey].hosLogs.length;
            const evictedViolationsSize = trackedDays[oldestKey].violations.length;

            logger.event(
                logger.Event.HISTORICALDAYCACHE,
                JSON.stringify({
                    result: "evict",
                    reason: "fifo",
                    dateKey: oldestKey,
                    evictedSystemLogsCount: evictedSystemLogsSize,
                    evictedHosLogsCount: evictedHosLogsSize,
                    evictedViolationsCount: evictedViolationsSize,
                    totalSystemLogs,
                    totalHosLogs,
                    totalViolations,
                    totalDaysCached: trackedKeys.length
                }),
                getCurrentUser()
            );
            this.store.dispatch(HistoricalDayActions.untrackHistoricalDay({ userId, dateKey: oldestKey }));
            totalSystemLogs -= evictedSystemLogsSize;
            totalHosLogs -= evictedHosLogsSize;
            totalViolations -= evictedViolationsSize;
        }

        this.store.dispatch(HistoricalDayActions.trackHistoricalDay({
            userId,
            dateKey,
            loadedAt: Date.now(),
            hosLogs: writeLog.userHosLogs,
            systemLogs: writeLog.systemHosLogs,
            violations: writeLog.violations,
            odometer: writeLog.odometer
        }));

        logger.event(
            logger.Event.HISTORICALDAYCACHE,
            JSON.stringify({
                result: "write",
                dateKey,
                systemLogs: writeLog.systemHosLogs.length,
                hosLogs: writeLog.userHosLogs.length,
                violations: writeLog.violations.length,
                totalSystemLogs: totalSystemLogs + writeLog.systemHosLogs.length,
                totalHosLogs: totalHosLogs + writeLog.userHosLogs.length,
                totalViolations: totalViolations + writeLog.violations.length,
                totalDaysCached: Object.keys(this.store.getState().historicalDayTracker[userId] || {}).length
            }),
            getCurrentUser()
        );
    }

    /**
     * Returns the most recent log whose current status carries into the selected day, or null when
     * none is found. Requested adds are skipped (a pending new log is not part of the accepted
     * timeline yet), but requested edits to existing logs are included because their current
     * (pre-edit) status is what continues until the edit is accepted.
     *
     * Membership in STANDARD_STATUSES mirrors the sync's `canBePlaceholder` filter, so point-in-time
     * control records (Login/Logoff/Certify, engine events, exemptions, etc.) are never treated as a
     * boundary. `isRegularDisplayLog()` (DISPLAY_STATUSES) is broader and would wrongly include them.
     */
    private findBoundaryLog(logs: DutyStatusLog[], dayStart: Date): DutyStatusLog | null {
        const dayStartMs = dayStart.getTime();
        let boundary: DutyStatusLog | null = null;
        for (const log of logs) {
            // No isPlaceholder() guard: these logs come straight from the server, and placeholders
            // are a client-side sync construct — none can appear here.
            if (log.isRemoved() || log.requestedAdd || STANDARD_STATUSES.indexOf(log.status) === -1) {
                continue;
            }
            if (log.getDate().getTime() >= dayStartMs) {
                continue;
            }
            if (!boundary || log.getDate().getTime() > boundary.getDate().getTime()) {
                boundary = log;
            }
        }
        return boundary;
    }

    /**
     * Synthesizes a "continued from previous day" placeholder for the selected day from the
     * boundary log, mirroring the placeholder properties the sync process builds for in-window days
     * (status/device/driver/coDrivers/isIgnored). The placeholder reflects the current accepted
     * status, so while a requested edit on an earlier day is pending it shows the pre-edit status
     * and, once the edit is accepted and the cache refreshed, it shows the updated status.
     *
     * The placeholder is marked certified under the same rule as the in-window sync
     * ({@link isPlaceholderCertified}); without it a certified day renders as Uncertified.
     *
     * Returns null when there is no boundary log, or when the selected day already begins with a
     * regular display log at (or before) the start of the day — in which case no continuation is
     * needed.
     */
    private buildContinuationPlaceholder(
        boundaryLog: DutyStatusLog | null,
        dayFromDate: Date,
        dayUserLogs: DutyStatusLog[],
        dateKey: string,
        certifyLogs: DutyStatusLog[]
    ): DutyStatusLog | null {
        if (!boundaryLog) {
            return null;
        }

        // dayUserLogs is pre-filtered to [dayFrom, dayTo), so no log precedes dayStartMs; the
        // === check captures the only reachable case: a log starting exactly at the day boundary.
        const dayStartMs = dayFromDate.getTime();
        const startsWithRealLog = dayUserLogs.some(
            log => STANDARD_STATUSES.indexOf(log.status) > -1 && log.getDate().getTime() === dayStartMs
        );
        if (startsWithRealLog) {
            return null;
        }

        return new DutyStatusLog({
            ...(isPlaceholderCertified(boundaryLog, dayFromDate, certifyLogs) ? { verifyDateTime: dayFromDate.toISOString() } : {}),
            id: `${ boundaryLog.id }_placeholder_${ dateKey }`,
            parentId: boundaryLog.id,
            placeholder: true,
            dateTime: dayFromDate.toISOString(),
            status: boundaryLog.status,
            device: boundaryLog.device,
            driver: boundaryLog.driver,
            coDrivers: boundaryLog.coDrivers,
            isIgnored: boundaryLog.isIgnored,
            origin: boundaryLog.origin
        } as IDutyStatusLog);
    }

    private separateLogs(logs: DutyStatusLog[]): IHistoricalDayResult {
        const userHosLogs: DutyStatusLog[] = [];
        const systemHosLogs: DutyStatusLog[] = [];

        for (const log of logs) {
            if (log.isRegularDisplayLog()) {
                userHosLogs.push(log);
            } else {
                systemHosLogs.push(log);
            }
        }

        return { userHosLogs, systemHosLogs };
    }
}
